<template>
  <div class="tutorial-container">
    <!-- Top-level Help sections -->
    <div class="section-tabs" role="tablist" aria-label="Help sections">
      <button
        v-for="tab in sectionTabs"
        :key="tab.id"
        type="button"
        role="tab"
        class="section-tab"
        :class="{ active: activeSection === tab.id }"
        :aria-selected="activeSection === tab.id"
        @click="activeSection = tab.id"
      >
        <i :class="tab.icon" aria-hidden="true"></i>
        <span>{{ tab.label }}</span>
      </button>
    </div>

    <div class="tutorial-scroll">
      <!-- Getting Started -->
      <section v-show="activeSection === 'started'" class="help-panel" role="tabpanel">
        <div v-if="isMobile" class="tutorial-steps">
          <div class="tutorial-step">
            <div class="step-number">1</div>
            <div class="step-content">
              <h3>Best experience on a computer</h3>
              <p>
                Saving jobs from LinkedIn, Indeed, and Glassdoor works with the ResyMaid Chrome extension on a desktop or laptop.
                You can’t complete that part of the flow from your phone.
              </p>
              <div class="step-details">
                <div class="detail-item">
                  <i class="fas fa-desktop"></i>
                  <span>On a computer, install the Chrome extension and pin it to your toolbar</span>
                </div>
                <div class="detail-item">
                  <i class="fas fa-briefcase"></i>
                  <span>Browse job sites and save roles with one click — they sync to this dashboard</span>
                </div>
              </div>
            </div>
          </div>

          <div class="tutorial-step">
            <div class="step-number">2</div>
            <div class="step-content">
              <h3>Set up your resume here</h3>
              <p>Upload and edit your base resume on your phone anytime so you’re ready when jobs land in Jobs.</p>
              <div class="step-details">
                <div class="detail-item">
                  <i class="fas fa-file-pdf"></i>
                  <span>Supports PDF, DOCX, and TXT</span>
                </div>
                <div class="detail-item">
                  <i class="fas fa-brain"></i>
                  <span>AI extracts skills, experience, and education</span>
                </div>
              </div>
              <button type="button" class="step-action" @click="$emit('navigate', 'SettingsComponent')">
                <i class="fas fa-upload"></i> Open My Resume
              </button>
            </div>
          </div>

          <div class="tutorial-step">
            <div class="step-number">3</div>
            <div class="step-content">
              <h3>Save jobs on your computer</h3>
              <p>
                When you find a role on LinkedIn, Indeed, or Glassdoor on your computer, save it with the extension.
                Those jobs show up here on your phone automatically.
              </p>
              <div class="step-details">
                <div class="detail-item">
                  <i class="fas fa-sync"></i>
                  <span>Same account — jobs appear on Jobs as soon as they’re saved</span>
                </div>
                <div class="detail-item">
                  <i class="fas fa-magic"></i>
                  <span>Title, company, and requirements are captured for you</span>
                </div>
              </div>
            </div>
          </div>

          <div class="tutorial-step">
            <div class="step-number">4</div>
            <div class="step-content">
              <h3>Generate &amp; apply on the go</h3>
              <p>
                From your phone, open Jobs, generate a tailored resume, download it, and apply — perfect when you’re commuting or away from your desk.
              </p>
              <div class="step-details">
                <div class="detail-item">
                  <i class="fas fa-wand-magic-sparkles"></i>
                  <span>One tap generates an ATS-ready resume for that job</span>
                </div>
                <div class="detail-item">
                  <i class="fas fa-download"></i>
                  <span>Download PDF, DOCX, or HTML, then apply from your phone</span>
                </div>
                <div class="detail-item">
                  <i class="fas fa-flag"></i>
                  <span>Update status (Applied, Interview, Offer) so Pipeline stays current</span>
                </div>
              </div>
              <button type="button" class="step-action" @click="$emit('navigate', 'JobsComponent')">
                <i class="fas fa-briefcase"></i> Open Jobs
              </button>
            </div>
          </div>
        </div>

        <div v-else class="tutorial-steps">
          <div class="tutorial-step">
            <div class="step-number">1</div>
            <div class="step-content">
              <h3>Install the Chrome Extension</h3>
              <p>Get the ResyMaid Chrome extension to save jobs directly from LinkedIn, Indeed, and Glassdoor.</p>
              <div class="step-details">
                <div class="detail-item">
                  <i class="fas fa-download"></i>
                  <span>Download from the Chrome Web Store</span>
                </div>
                <div class="detail-item">
                  <i class="fas fa-puzzle-piece"></i>
                  <span>Pin the extension to your toolbar for easy access</span>
                </div>
              </div>
              <a
                href="https://chromewebstore.google.com/detail/resymaid-ai-resume-builde/clpebgikifkkdipdhicplhibbffamfbm"
                target="_blank"
                rel="noopener noreferrer"
                class="step-action"
              >
                <i class="fab fa-chrome"></i> Get Chrome Extension
              </a>
            </div>
          </div>

          <div class="tutorial-step">
            <div class="step-number">2</div>
            <div class="step-content">
              <h3>Upload Your Base Resume</h3>
              <p>Upload your existing resume so our AI can learn about your experience, skills, and achievements.</p>
              <div class="step-details">
                <div class="detail-item">
                  <i class="fas fa-file-pdf"></i>
                  <span>Supports PDF, DOCX, and DOC formats</span>
                </div>
                <div class="detail-item">
                  <i class="fas fa-brain"></i>
                  <span>AI extracts your skills, experience, and education</span>
                </div>
              </div>
              <button type="button" class="step-action" @click="$emit('navigate', 'SettingsComponent')">
                <i class="fas fa-upload"></i> Go to Settings
              </button>
            </div>
          </div>

          <div class="tutorial-step">
            <div class="step-number">3</div>
            <div class="step-content">
              <h3>Save Jobs While Browsing</h3>
              <p>When you find a job you're interested in, click the ResyMaid extension to save it instantly.</p>
              <div class="step-details">
                <div class="detail-item">
                  <i class="fas fa-briefcase"></i>
                  <span>Works on LinkedIn, Indeed, and Glassdoor</span>
                </div>
                <div class="detail-item">
                  <i class="fas fa-magic"></i>
                  <span>Automatically extracts job title, company, and requirements</span>
                </div>
              </div>
            </div>
          </div>

          <div class="tutorial-step">
            <div class="step-number">4</div>
            <div class="step-content">
              <h3>Generate Tailored Resumes</h3>
              <p>With one click, our AI creates a resume perfectly tailored to each job's requirements.</p>
              <div class="step-details">
                <div class="detail-item">
                  <i class="fas fa-bullseye"></i>
                  <span>Highlights relevant skills and experience</span>
                </div>
                <div class="detail-item">
                  <i class="fas fa-chart-line"></i>
                  <span>Optimized for ATS (Applicant Tracking Systems)</span>
                </div>
                <div class="detail-item">
                  <i class="fas fa-download"></i>
                  <span>Download as PDF, DOCX, or HTML</span>
                </div>
              </div>
            </div>
          </div>

          <div class="tutorial-step">
            <div class="step-number">5</div>
            <div class="step-content">
              <h3>Track Your Applications</h3>
              <p>Keep all your job applications organized in one place. Never lose track of where you've applied.</p>
              <div class="step-details">
                <div class="detail-item">
                  <i class="fas fa-list-check"></i>
                  <span>View all saved jobs and generated resumes</span>
                </div>
                <div class="detail-item">
                  <i class="fas fa-clock"></i>
                  <span>See when you saved each job</span>
                </div>
              </div>
              <button type="button" class="step-action" @click="$emit('navigate', 'JobsComponent')">
                <i class="fas fa-briefcase"></i> View Jobs
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Using the Dashboard -->
      <section v-show="activeSection === 'dashboard'" class="help-panel" role="tabpanel">
        <div class="dashboard-guide">
          <div class="guide-tabs" role="tablist" aria-label="Dashboard sections">
            <button
              v-for="tab in guideTabs"
              :key="tab.id"
              type="button"
              role="tab"
              class="guide-tab"
              :class="{ active: activeGuideTab === tab.id }"
              :aria-selected="activeGuideTab === tab.id"
              @click="activeGuideTab = tab.id"
            >
              <i :class="tab.icon" aria-hidden="true"></i>
              <span>{{ tab.label }}</span>
            </button>
          </div>

          <div class="guide-panel tutorial-step" role="tabpanel">
            <div class="step-number guide-icon"><i :class="activeGuideContent.icon"></i></div>
            <div class="step-content">
              <h3>{{ activeGuideContent.title }}</h3>
              <p>{{ activeGuideContent.body }}</p>
              <div class="step-details">
                <div
                  v-for="(detail, idx) in activeGuideContent.details"
                  :key="idx"
                  class="detail-item"
                >
                  <i :class="detail.icon"></i>
                  <span>{{ detail.text }}</span>
                </div>
              </div>
              <button
                v-if="activeGuideContent.actionView"
                type="button"
                class="step-action"
                @click="$emit('navigate', activeGuideContent.actionView)"
              >
                <i :class="activeGuideContent.actionIcon"></i>
                {{ activeGuideContent.actionLabel }}
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Pro Tips -->
      <section v-show="activeSection === 'tips'" class="help-panel" role="tabpanel">
        <div class="tips-section">
          <div class="tips-grid">
            <div class="tip-card">
              <div class="tip-leading">
                <div class="tip-icon">
                  <i class="fas fa-fw fa-coins"></i>
                </div>
                <h4>Save Credits</h4>
              </div>
              <div class="tip-divider" aria-hidden="true"></div>
              <p class="tip-desc">
                Each resume generation uses 1 credit. New accounts start with 3 free credits to try it out!
              </p>
            </div>
            <div class="tip-card">
              <div class="tip-leading">
                <div class="tip-icon">
                  <i class="fas fa-fw fa-user-friends"></i>
                </div>
                <h4>Earn Free Credits</h4>
              </div>
              <div class="tip-divider" aria-hidden="true"></div>
              <p class="tip-desc">
                Refer friends and earn 10 free credits for each friend who signs up and makes a purchase.
              </p>
            </div>
            <div class="tip-card">
              <div class="tip-leading">
                <div class="tip-icon">
                  <i class="fas fa-fw fa-file-alt"></i>
                </div>
                <h4>Keep Resume Updated</h4>
              </div>
              <div class="tip-divider" aria-hidden="true"></div>
              <p class="tip-desc">
                Update your base resume in My Resume whenever you gain new skills or experience for better results.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { inject, ref, computed, unref } from 'vue';

export default {
  name: 'TutorialComponent',
  emits: ['navigate'],
  setup() {
    const isMobileRef = inject('isMobile', ref(false));
    const isMobile = computed(() => !!unref(isMobileRef));
    const activeSection = ref('started');
    const activeGuideTab = ref('jobs');

    const sectionTabs = [
      { id: 'started', label: 'Getting Started', icon: 'fas fa-rocket' },
      { id: 'dashboard', label: 'Dashboard', icon: 'fas fa-th-large' },
      { id: 'tips', label: 'Pro Tips', icon: 'fas fa-lightbulb' },
    ];

    const guideTabs = [
      { id: 'jobs', label: 'Jobs', icon: 'fas fa-briefcase' },
      { id: 'status', label: 'Status', icon: 'fas fa-flag' },
      { id: 'pipeline', label: 'Pipeline', icon: 'fas fa-chart-line' },
      { id: 'resume', label: 'Resume', icon: 'fas fa-file-alt' },
    ];

    const guideContentById = computed(() => {
      const mobile = isMobile.value;
      return {
        jobs: {
          icon: 'fas fa-briefcase',
          title: mobile ? 'Jobs — your list on the go' : 'Jobs tab — your application list',
          body: mobile
            ? 'Jobs you saved on your computer appear here. Search, generate resumes, download, and open the posting to apply from your phone.'
            : 'Every job you save from the extension appears here. Search, sort, generate resumes, and download tailored versions.',
          details: mobile
            ? [
                { icon: 'fas fa-search', text: 'Search by title or company' },
                { icon: 'fas fa-wand-magic-sparkles', text: 'Tap Generate to create a tailored resume (1 credit)' },
                { icon: 'fas fa-download', text: 'Download PDF/DOCX/HTML, then apply from your phone' },
                { icon: 'fas fa-external-link-alt', text: 'Use View to open the original job posting' },
              ]
            : [
                { icon: 'fas fa-search', text: 'Use the search bar to filter by title or company' },
                { icon: 'fas fa-arrows-alt-v', text: 'Scroll vertically inside the table when you have many jobs' },
                { icon: 'fas fa-arrows-alt-h', text: 'Scroll horizontally to see Status, Resume, URL, and more' },
                { icon: 'fas fa-thumbtack', text: 'Column headers stay pinned at the top while you scroll' },
              ],
          actionView: 'JobsComponent',
          actionIcon: 'fas fa-briefcase',
          actionLabel: 'Open Jobs',
        },
        status: {
          icon: 'fas fa-flag',
          title: 'Track status on each job',
          body: 'Use Status to record where each application stands. You can update status even before generating a resume.',
          details: [
            { icon: 'fas fa-bookmark', text: 'Saved — job saved, no tailored resume yet' },
            { icon: 'fas fa-file-alt', text: 'Ready — resume generated' },
            { icon: 'fas fa-paper-plane', text: 'Applied → Interview → Offer — update as you progress' },
            { icon: 'fas fa-times-circle', text: 'Passed — role closed or you moved on' },
          ],
          actionView: 'JobsComponent',
          actionIcon: 'fas fa-briefcase',
          actionLabel: 'Open Jobs',
        },
        pipeline: {
          icon: 'fas fa-chart-line',
          title: mobile ? 'Pipeline — big picture' : 'Pipeline tab — see the big picture',
          body: 'See how your jobs are distributed across stages — saved, ready, applied, interview, offer, and passed.',
          details: mobile
            ? [
                { icon: 'fas fa-project-diagram', text: 'Green flows show progress; red shows Passed' },
                { icon: 'fas fa-sync', text: 'Update statuses on Jobs to refresh the chart' },
              ]
            : [
                { icon: 'fas fa-project-diagram', text: 'Green flows show forward progress; red shows jobs marked Passed' },
                { icon: 'fas fa-mouse-pointer', text: 'Hover bands for exact counts. Update statuses on Jobs to refresh' },
              ],
          actionView: 'PipelineComponent',
          actionIcon: 'fas fa-chart-line',
          actionLabel: 'Open Pipeline',
        },
        resume: {
          icon: 'fas fa-file-alt',
          title: 'My Resume, Account & credits',
          body: mobile
            ? 'Keep your base resume current under My Resume. Buy credits and manage referrals under Account.'
            : 'Upload or edit your base resume under My Resume. Buy credits and manage referrals under Account.',
          details: [
            { icon: 'fas fa-wand-magic-sparkles', text: 'Each tailored resume uses 1 credit — new accounts start with 3 free' },
            { icon: 'fas fa-percent', text: 'Match scores on each job show how well your profile fits the role' },
          ],
          actionView: 'SettingsComponent',
          actionIcon: 'fas fa-file-alt',
          actionLabel: 'Open My Resume',
        },
      };
    });

    const activeGuideContent = computed(() => guideContentById.value[activeGuideTab.value]);

    return {
      isMobile,
      activeSection,
      sectionTabs,
      activeGuideTab,
      guideTabs,
      activeGuideContent,
    };
  },
};
</script>

<style scoped>
.tutorial-container {
  width: 100%;
  max-width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
}

.tutorial-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding-bottom: 24px;
}

.section-tabs {
  display: flex;
  gap: 8px;
  flex: 0 0 auto;
  flex-shrink: 0;
  align-self: stretch;
  align-items: center;
  height: auto;
  min-height: 0;
  max-height: none;
  padding: 12px 4px 14px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.section-tabs::-webkit-scrollbar {
  display: none;
}

.section-tab {
  flex: 1 1 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 0;
  min-height: 40px;
  max-height: none;
  padding: 10px 14px;
  border: 1px solid var(--app-border);
  border-radius: 10px;
  background: var(--app-surface);
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
  box-shadow: none;
  outline: none;
  -webkit-tap-highlight-color: transparent;
}

.section-tab i {
  font-size: 14px;
  flex-shrink: 0;
}

.section-tab span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.section-tab:focus,
.section-tab:focus-visible {
  outline: none;
  box-shadow: none;
}

.section-tab.active {
  color: var(--app-primary);
  border-color: color-mix(in srgb, var(--app-primary) 35%, var(--app-border));
  background: color-mix(in srgb, var(--app-primary) 10%, var(--app-surface));
  box-shadow: none;
}

.tutorial-container * {
  box-sizing: border-box;
  max-width: 100%;
}

.help-panel {
  width: 100%;
}

.tutorial-steps {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 8px;
  padding: 0 4px;
}

.tutorial-step {
  display: flex;
  gap: 14px;
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  border-radius: var(--card-radius, 12px);
  padding: 16px;
  width: 100%;
  box-shadow: var(--shadow-card);
  overflow: visible;
}

.step-number {
  width: 36px;
  height: 36px;
  background: var(--surface-3, #f2f2f7);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  color: var(--app-primary);
  flex-shrink: 0;
}

.step-content {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.step-content h3 {
  color: var(--app-text);
  font-size: 17px;
  font-weight: 600;
  margin: 0 0 6px 0;
  letter-spacing: -0.2px;
}

.step-content > p {
  color: var(--text-secondary);
  font-size: 15px;
  margin: 0 0 12px 0;
  line-height: 1.4;
}

.step-details {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}

.detail-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: var(--text-secondary);
  font-size: 15px;
  line-height: 1.35;
}

.detail-item i {
  color: var(--app-primary);
  width: 18px;
  text-align: center;
  flex-shrink: 0;
  margin-top: 2px;
}

.step-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  min-height: var(--control-height, 44px);
  background: var(--surface-2, #ffffff);
  border: 1px solid var(--app-border);
  border-radius: 10px;
  color: var(--app-primary);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  font: inherit;
  transition: background 0.2s;
  box-shadow: none;
}

.step-action:hover {
  background: var(--surface-3, #f2f2f7);
  color: var(--app-primary-hover);
}

.dashboard-guide {
  margin-bottom: 8px;
  padding: 8px 4px 0;
}

.guide-tabs {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding: 0 0 12px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.guide-tabs::-webkit-scrollbar {
  display: none;
}

.guide-tab {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 0;
  min-height: 52px;
  padding: 8px 6px;
  border: 1px solid var(--app-border);
  border-radius: 10px;
  background: var(--app-surface);
  color: var(--text-secondary);
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
}

.guide-tab i {
  font-size: 16px;
}

.guide-tab.active {
  color: var(--app-primary);
  border-color: color-mix(in srgb, var(--app-primary) 40%, var(--app-border));
  background: var(--surface-3, #f2f2f7);
}

.guide-panel {
  margin: 0;
}

.guide-icon {
  font-size: 18px;
}

.tips-section {
  background: transparent;
  border: none;
  padding: 8px 4px;
  margin-top: 0;
}

.tips-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  padding: 0;
}

.tip-card {
  display: grid;
  grid-template-columns: 92px 1px 1fr;
  align-items: center;
  gap: 0 12px;
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  border-radius: var(--card-radius, 12px);
  padding: 12px 14px;
  box-shadow: var(--shadow-card);
  text-align: left;
}

.tip-leading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 8px;
  width: 92px;
  max-width: 92px;
  min-width: 92px;
  padding: 0;
  box-sizing: border-box;
}

.tip-icon {
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  background: var(--surface-3, #f2f2f7);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
}

.tip-icon i {
  font-size: 15px;
  width: 15px;
  height: 15px;
  line-height: 15px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--app-primary);
}

.tip-card h4 {
  color: var(--app-text);
  font-size: 12px;
  font-weight: 600;
  margin: 0;
  line-height: 1.25;
  width: 100%;
  max-width: 100%;
}

.tip-divider {
  width: 1px;
  align-self: stretch;
  background: var(--app-border);
  justify-self: center;
}

.tip-desc {
  color: var(--text-secondary);
  font-size: 13px;
  margin: 0;
  line-height: 1.45;
}

@media (min-width: 901px) {
  .tips-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 600px) {
  .section-tab {
    min-height: 34px;
    max-height: 36px;
    padding: 5px 6px;
    font-size: 11px;
    gap: 4px;
  }

  .section-tab i {
    font-size: 12px;
  }

  .section-tab span {
    font-size: 10px;
  }

  .dashboard-guide {
    padding: 4px 4px 0;
  }

  .tips-section {
    padding: 4px 4px 12px;
  }

  .step-action {
    width: 100%;
  }

  .tip-card {
    grid-template-columns: 92px 1px 1fr;
    gap: 0 10px;
    padding: 12px;
  }

  .tip-leading {
    width: 92px;
    max-width: 92px;
    min-width: 92px;
  }

  .tip-card h4 {
    font-size: 12px;
  }

  .tip-desc {
    font-size: 13px;
  }
}
</style>
